import React, { useEffect, useState } from 'react';
import api from './api';
import Modal from './components/Modal';
import toast from 'react-hot-toast';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';

const Users = () => {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'user', perm_users: true, perm_licenses: true, perm_devices: true, perm_apps: true, perm_messaging: true });
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const requestorId = currentUser.id;

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const { data } = await api.get('/users');
            setUsers(data);
        } catch (err) {
            toast.error('Failed to load users');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.put(`/users/${editingId}`, formData);
                toast.success('User updated successfully');
            } else {
                await api.post('/users', formData);
                toast.success('User created successfully');
            }
            fetchUsers();
            setModalOpen(false);
            resetForm();
        } catch (err) {
            toast.error('Operation failed');
        }
    };

    const deleteUser = async (id) => {
        if (window.confirm('Delete user? This cannot be undone.')) {
            try {
                await api.delete(`/users/${id}`);
                toast.success('User deleted');
                fetchUsers();
            } catch (err) {
                toast.error('Failed to delete user');
            }
        }
    };

    const resetForm = () => {
        setFormData({ name: '', email: '', password: '', role: 'user', perm_users: true, perm_licenses: true, perm_devices: true, perm_apps: true, perm_messaging: true });
        setEditingId(null);
    };

    const openEdit = (user) => {
        setFormData({ 
            ...user, 
            password: '',
            perm_users: !!user.perm_users,
            perm_licenses: !!user.perm_licenses,
            perm_devices: !!user.perm_devices,
            perm_apps: !!user.perm_apps,
            perm_messaging: !!user.perm_messaging
        });
        setEditingId(user.id);
        setModalOpen(true);
    };

    const filteredUsers = users.filter(u => 
        u.name.toLowerCase().includes(search.toLowerCase()) || 
        u.email.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="module">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ margin: 0, border: 'none', padding: 0 }}>System Users</h2>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <div className="search-bar" style={{ position: 'relative' }}>
                        <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#45a29e' }} />
                        <input 
                            placeholder="Search Name or Email..." 
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ paddingLeft: '40px', width: '300px', margin: 0 }}
                        />
                    </div>
                    <button onClick={() => { resetForm(); setModalOpen(true); }} style={{ width: 'auto', margin: 0, background: '#66fcf1', color: '#0b0c10' }}><FaPlus /> New User</button>
                </div>
            </div>

            <table>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Licenses</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredUsers.map(u => (
                        <tr key={u.id}>
                            <td>{u.id}</td>
                            <td>{u.name}</td>
                            <td>{u.email}</td>
                            <td>{u.role ? String(u.role).toUpperCase() : ''}</td>
                            <td style={{ textAlign: 'center' }}>
                                <span style={{ background: 'rgba(102, 252, 241, 0.1)', color: '#66fcf1', padding: '2px 10px', borderRadius: '10px', fontSize: '0.85rem' }}>
                                    {u.license_count || 0}
                                </span>
                            </td>
                            <td><span className={`status ${u.status === 'active' ? 'active' : 'blocked'}`}>{u.status}</span></td>
                            <td>
                                <button onClick={() => openEdit(u)} style={{ width: 'auto', padding: '10px', marginRight: '10px', background: 'transparent', border: 'none' }} title="Edit/Permissions"><FaEdit color="#66fcf1" /></button>
                                {requestorId !== u.id && (
                                    <button onClick={() => deleteUser(u.id)} style={{ width: 'auto', padding: '10px', background: 'transparent', border: 'none' }} title="Delete"><FaTrash color="#ff4d4d" /></button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* User Edit Modal */}
            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} title={editingId ? "Edit User" : "Add New User"}>
                <form onSubmit={handleSave}>
                    <input placeholder="Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                    <input placeholder="Email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                    <input placeholder={editingId ? "New Password (leave blank to keep)" : "Password"} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required={!editingId} />

                    <select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                    </select>

                    {formData.role === 'admin' && (
                        <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '4px' }}>
                            <strong style={{ color: '#66fcf1', display: 'block', marginBottom: '10px' }}>Admin Permissions (Modules)</strong>
                            {['users', 'licenses', 'devices', 'apps', 'messaging'].map(mod => (
                                <label key={mod} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#c5c6c7', marginBottom: '5px', cursor: 'pointer', textTransform: 'capitalize' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData[`perm_${mod}`]}
                                        onChange={e => setFormData({ ...formData, [`perm_${mod}`]: e.target.checked })}
                                        style={{ width: '15px', margin: 0 }}
                                    />
                                    Manage {mod}
                                </label>
                            ))}
                        </div>
                    )}

                    <button type="submit" style={{ width: '100%' }}>{editingId ? "Update User" : "Create User"}</button>
                </form>
            </Modal>
        </div>
    );
};

export default Users;
